const REDACTED_VALUE = '[REDACTED]';
const ALWAYS_SENSITIVE_PARTS = [
  'password', 'passwd', 'passcode', 'pin', 'otp', 'token', 'authorization', 'cookie',
  'secret', 'apikey', 'credential', 'privatekey', 'cardnumber', 'cardno', 'rfid',
  'bankaccount', 'accountnumber',
];
const PERSONAL_IDENTITY_PARTS = ['phone', 'email'];

function normalizeKey(key) {
  return String(key || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

function matchesPart(key, parts) {
  const normalized = normalizeKey(key);
  return parts.some((part) => normalized.includes(part));
}

function isSensitiveKey(key, { revealPersonalData = false } = {}) {
  return matchesPart(key, ALWAYS_SENSITIVE_PARTS)
    || (!revealPersonalData && matchesPart(key, PERSONAL_IDENTITY_PARTS));
}

function redactDetails(value, currentKey = '', options = {}) {
  if (isSensitiveKey(currentKey, options)) return REDACTED_VALUE;
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.map((item) => redactDetails(item, '', options));
  if (typeof value === 'object') {
    return Object.entries(value).reduce((result, [key, item]) => {
      result[key] = redactDetails(item, key, options);
      return result;
    }, {});
  }
  return value;
}

function summarizeDetails(details = {}, options = {}) {
  const changedFields = new Set();
  [details?.oldValue, details?.newValue].forEach((value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return;
    Object.keys(value).forEach((key) => {
      if (!isSensitiveKey(key, options)) changedFields.add(key);
    });
  });
  const sorted = Array.from(changedFields).sort();
  return {
    changedFields: sorted,
    changeCount: sorted.length,
    hasDetails: details !== null && details !== undefined
      && (typeof details !== 'object' || Object.keys(details).length > 0),
  };
}

export function presentAuditLog(log = {}, options = {}) {
  const details = redactDetails(log.details, '', options);
  const localSummary = summarizeDetails(details, options);
  const serverFields = Array.isArray(log.summary?.changedFields)
    ? log.summary.changedFields.filter((key) => !isSensitiveKey(key, options)).sort()
    : localSummary.changedFields;
  return {
    ...log,
    details,
    summary: {
      ...localSummary,
      ...log.summary,
      changedFields: serverFields,
      changeCount: serverFields.length,
      hasDetails: log.summary?.hasDetails ?? localSummary.hasDetails,
    },
  };
}

export function normalizeAuditResponse(payload = {}) {
  const options = { revealPersonalData: payload.viewer?.personalIdentityVisible === true };
  const logs = Array.isArray(payload.data) ? payload.data.map((log) => presentAuditLog(log, options)) : [];
  const pagination = payload.pagination || {
    total: Number(payload.total || logs.length),
    page: Number(payload.page || 1),
    limit: 20,
    pages: Number(payload.pages || 1),
  };
  return {
    logs,
    viewer: payload.viewer || { personalIdentityVisible: false, credentialsAlwaysHidden: true },
    pagination: {
      total: Number(pagination.total || 0),
      page: Number(pagination.page || 1),
      limit: Number(pagination.limit || 20),
      pages: Math.max(Number(pagination.pages || 1), 1),
    },
  };
}

export { REDACTED_VALUE, redactDetails };
